import { consoleRoutes } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, buttonClasses, EmptyState, ErrorState, Field, Input, Select, Skeleton } from "@sagot/ui";
import { useState } from "react";
import { usePermissions } from "../../../../session/permissions";
import { useWorkspaceContext } from "../../../../session/workspace-context";
import { useDebounced } from "../../../../lib/slug";
import { useArticleSources } from "../use-article-sources";
import { nameOf, useRefs } from "./refs";

const A = "engage.workflows.article.source";

interface SourceFieldProps {
  value: string;
  disabled: boolean;
  onChange: (sourceId: string) => void;
}

/**
 * The knowledge source an article card links: only ready websites with a public https address are offered (a file lives in
 * private storage and has no link a visitor could open). A search box narrows the list; a source that is gone, not ready or no
 * longer public stays visible as the current choice, with the reason to pick another.
 */
export function ArticleSourceField({ value, disabled, onChange }: SourceFieldProps) {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  const permissions = usePermissions();
  const refs = useRefs();
  const [search, setSearch] = useState("");
  const query = useDebounced(search.trim());
  const readable = permissions.can("knowledge.view");
  const sources = useArticleSources(query, readable);
  const firstAgent = refs.agents[0]?.id;
  const knowledge = firstAgent ? consoleRoutes.knowledge(summary.slug, firstAgent) : null;

  if (permissions.isPending || (readable && sources.isPending && value === "")) {
    return (
      <div role="status" aria-label={t(`${A}.loading`)} className="space-y-2">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }
  if (!readable) return <p className="rounded-md bg-surface-2 p-3 text-xs text-fg-muted" role="note">{t(`${A}.noAccess`)}</p>;
  if (sources.isError) return <ErrorState headingLevel={3} title={t(`${A}.loadFailedTitle`)} description={t(`${A}.loadFailedBody`)} retryLabel={t(`${A}.retry`)} onRetry={() => void sources.refetch()} />;
  const found = sources.data ?? [];
  if (found.length === 0 && query === "" && value === "") {
    return (
      <EmptyState
        icon="globe"
        headingLevel={3}
        title={t(`${A}.emptyTitle`)}
        description={t(`${A}.emptyBody`)}
        action={knowledge ? <AppLink className={buttonClasses({ variant: "secondary", size: "sm" })} href={knowledge}>{t(`${A}.openKnowledge`)}</AppLink> : undefined}
      />
    );
  }
  const listed = found.some((source) => source.id === value);
  const known = refs.sources?.some((source) => source.id === value) ?? false;
  const gone = value !== "" && !known && !refs.loading && refs.sources !== undefined && !listed;
  return (
    <div className="space-y-2">
      <Field label={t(`${A}.search`)}>
        <Input type="search" value={search} disabled={disabled} placeholder={t(`${A}.searchPlaceholder`)} onChange={(event) => { setSearch(event.target.value); }} />
      </Field>
      <Field label={t(`${A}.label`)} hint={t(`${A}.hint`)} error={gone ? t(`${A}.unavailable`) : undefined}>
        <Select value={value} disabled={disabled} onChange={(event) => { onChange(event.target.value); }}>
          <option value="">{t(`${A}.choose`)}</option>
          {value !== "" && !listed ? <option value={value}>{gone ? t(`${A}.unavailableOption`) : nameOf(refs.sources ?? [], value, t(`${A}.unavailableOption`))}</option> : null}
          {found.map((source) => <option key={source.id} value={source.id}>{source.title}</option>)}
        </Select>
      </Field>
      {found.length === 0 && query !== "" ? <p className="text-xs text-fg-muted" role="status">{t(`${A}.noMatches`, { query })}</p> : null}
    </div>
  );
}
